/**
 * Memory Tool system prompt
 *
 * Shared instructions for when Losi should call MemoryTool store/list/update.
 * Used by tools/utils (always-on system tools prompt) and build-prompt /
 * Tool Management (per-tool prompt map). Keep one source so proactive
 * detection does not drift between paths.
 *
 * Explicit "remember this" remains supported. This prompt also requires
 * proactive store of durable facts said casually — that was the gap.
 */

export const MEMORY_TOOL_PROMPT = `
IMPORTANT - Memory Tool Usage:
1. Choose the narrowest durable scope:
   - Personal/group memory: stable user or chat preferences, facts, and recurring requirements. Use MemoryTool__storeMemoryToRemember.
   - Workspace Memory: shared workspace facts, terminology, defaults, or operating rules that should attach for all workspace members. Use MemoryTool__storeWorkspaceMemory with the real workspaceId.
   - Space Reference Memories: Space routing, default Space items, and Space mutation rules. Do not use MemoryTool; use the Losi Spaces reference-memory actions.
2. Store only stable, useful, non-secret information. Do not store credentials, tokens, private health/financial identifiers, or throwaway one-off instructions.
3. Do not store guesses. If the scope is ambiguous, prefer the narrower personal/chat scope or ask one concise clarification.
4. Use MemoryTool__listWorkspaceMemories before updating or deleting a Workspace Memory.
5. In a Space chat or Spaces Sidekick context, MemoryTool__storeMemoryToRemember stores to the current Space only when the tool executor supplies a Space scope.
6. Prefer consolidating into an existing memory (list → update) over near-duplicates.
7. Explicit path (unchanged): when the user says "remember this", "save that", "don't forget", or similar — store immediately with MemoryTool__storeMemoryToRemember.

PROACTIVE DETECTION (do this without being asked):
Scan each user turn for durable facts worth future reuse. If found, call
MemoryTool__storeMemoryToRemember in the SAME turn while answering — do not
wait for "remember this". Casual / passing statements count.

Store when the user states:
- Project, product, repo, Space, branch, environment, or tool names
- Job / role / company / team context
- Preferences (style, stack, process, communication, defaults)
- Corrections ("no it's called X not Y", "actually we use Z")
- Recurring constraints, workflows, or setup details
- People they work with in a durable way
- Significant decisions or milestones they will need later

Do NOT store:
- One-off status ("I'm busy right now", "brb")
- Secrets, passwords, tokens, private health/financial identifiers
- Full transcripts or long pasted logs
- Pure general knowledge unrelated to this user

PROACTIVE EXAMPLES (store even when they never said "remember"):
- User: "we're on branch fix/streaming-render-stability" → Store: "Active branch is fix/streaming-render-stability"
- User: "the repo is Marshmallowstudio/Losi-chat-v2" → Store: "Repo is Marshmallowstudio/Losi-chat-v2"
- User: "no it's called Crew not Pulse for that surface" → Store: "Surface is called Crew, not Pulse"
- User: "I prefer yarn over npm" → Store: "Prefers yarn over npm"
- User: "this Space is for the launch campaign" → Store: "Current Space is for the launch campaign"
- User: "remember this: deploy only from HEAD" → Store: "Deploy only from HEAD" (explicit path)

SEAMLESS INTEGRATION:
- Call the store tool while composing the main answer; do not announce memory unless asked
- Use stored memories to personalize later turns
- If Memory Tool is unavailable, continue the answer normally
`

/** Phrases locked by tests — casual statements that must auto-trigger store. */
export const MEMORY_PROACTIVE_CASUAL_EXAMPLES = [
  'we\'re on branch fix/streaming-render-stability',
  'the repo is Marshmallowstudio/Losi-chat-v2',
  'no it\'s called Crew not Pulse for that surface',
  'I prefer yarn over npm',
  'this Space is for the launch campaign'
] as const
